import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import ApiService from '../../../../services/ApiService'
import BuyOrderSection, {
  BuyOrderBadge,
} from '../../../../components/ProductionFlow/ViewOPServiceModal/BuyOrderSection'
import { ActionButton } from '../../../../components/ProductionFlow/ViewOPServiceModal/BuyOrderSection/style'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  SimpleTable,
  SimpleTableRow,
  TableHeader,
  TableCell,
  Section,
  SectionDivider,
  SectionSubtitle,
} from '../../../../components/ProductionFlow/ViewOPServiceModal/style'
import { SplitCheckbox } from './style'
import OrdenoLoading from '../../../../components/OrdenoLoading'

interface BuyOrderData {
  id: number
  stockInProductionId: number
  quantityRequired: number | null
  quantityToBuy: number | null
  unitPrice: number | null
  totalPrice: number | null
  unitOfMeasurement: string | null
  status: string
  observations: string | null
  createdAt: string
  CreatedBy?: { id: number; name: string }
  StockInProduction?: {
    id: number
    stockId: number | null
    resourceId: number | null
    usedQuantity: number
    usedUnitOfMeasurement: string
    Stock?: { id: number; Code?: { code?: string } | null } | null
    Resource?: { id: number; name?: string } | null
  }
}

interface FashionBuyOrdersSectionProps {
  servicesId: number
  type?: 'materials' | 'resources' | 'all'
}

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #f5f9ff;
  border: 1px solid #cce4f6;
  border-radius: 8px;
`

const BulkSelectionCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #303030;
  margin-right: auto;
`

const CheckboxCell = styled(TableCell)`
  width: 40px;
  text-align: center;
  padding: 8px 4px !important;
`

const getMaterialName = (stockItem: any): string => {
  const stock = stockItem?.Stock
  return (
    stock?.Code?.code ||
    (stockItem?.stockId ? `Material ID: ${stockItem.stockId}` : 'Material não identificado')
  )
}

const getResourceName = (stockItem: any): string => {
  const resource = stockItem?.Resource
  return (
    resource?.name ||
    (stockItem?.resourceId ? `Insumo ID: ${stockItem.resourceId}` : 'Insumo não identificado')
  )
}

const isAbatableStatus = (status: string) =>
  status === 'PENDING' || status === 'DELIVERED'

const FashionBuyOrdersSection: React.FC<FashionBuyOrdersSectionProps> = ({
  servicesId,
  type = 'all',
}) => {
  const { setFeedback } = useContext(FeedbackContext)
  const [buyOrders, setBuyOrders] = useState<BuyOrderData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [selectedBuyOrderIds, setSelectedBuyOrderIds] = useState<number[]>([])
  const [isBulkRunning, setIsBulkRunning] = useState(false)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })

  const fetchBuyOrders = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.buyOrder.getByProduction(servicesId)
      setBuyOrders(response.data || [])
    } catch (e) {
      console.error('Erro ao carregar ordens de compra:', e)
      setError('Falha ao carregar ordens de compra.')
    } finally {
      setIsLoading(false)
    }
  }, [servicesId])

  useEffect(() => {
    fetchBuyOrders()
  }, [fetchBuyOrders])

  const toggleRow = (stockInProductionId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [stockInProductionId]: !prev[stockInProductionId],
    }))
  }

  const getBuyOrderFor = (stockInProductionId: number) =>
    buyOrders.find((bo) => bo.stockInProductionId === stockInProductionId) || null

  const stockInProductions = buyOrders
    .map((bo) => bo.StockInProduction)
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  const seen = new Set<number>()
  const dedupStockInProductions = stockInProductions.filter((s) => {
    if (seen.has(s.id)) return false
    seen.add(s.id)
    return true
  })

  const materials = dedupStockInProductions.filter((s) => s.stockId !== null)
  const resources = dedupStockInProductions.filter((s) => s.resourceId !== null)

  const selectedBuyOrders = useMemo(
    () => buyOrders.filter((bo) => selectedBuyOrderIds.includes(bo.id)),
    [buyOrders, selectedBuyOrderIds],
  )

  const canBulkPurchase =
    selectedBuyOrders.length > 0 &&
    selectedBuyOrders.every((bo) => bo.status === 'PENDING')

  const canBulkConsume =
    selectedBuyOrders.length > 0 &&
    selectedBuyOrders.every((bo) => isAbatableStatus(bo.status))

  const bulkPurchaseDisabledReason = useMemo(() => {
    if (selectedBuyOrders.length === 0) return ''
    if (canBulkPurchase) return ''
    return 'Compra em lote só é permitida quando todas as OCs selecionadas estão Pendentes.'
  }, [selectedBuyOrders.length, canBulkPurchase])

  const bulkConsumeDisabledReason = useMemo(() => {
    if (selectedBuyOrders.length === 0) return ''
    if (canBulkConsume) return ''
    return 'Abate em lote só é permitido quando todas as OCs selecionadas estão Pendentes ou Compradas.'
  }, [selectedBuyOrders.length, canBulkConsume])

  const toggleBuyOrderSelection = (buyOrderId: number) => {
    setSelectedBuyOrderIds((prev) =>
      prev.includes(buyOrderId)
        ? prev.filter((id) => id !== buyOrderId)
        : [...prev, buyOrderId],
    )
  }

  const toggleSelectAllInTable = (rows: typeof dedupStockInProductions) => {
    const idsInTable = rows
      .map((row) => getBuyOrderFor(row.id)?.id)
      .filter((id): id is number => id != null)

    const allSelected = idsInTable.every((id) => selectedBuyOrderIds.includes(id))

    if (allSelected) {
      setSelectedBuyOrderIds((prev) => prev.filter((id) => !idsInTable.includes(id)))
    } else {
      setSelectedBuyOrderIds((prev) => [...new Set([...prev, ...idsInTable])])
    }
  }

  const clearSelection = () => setSelectedBuyOrderIds([])

  const executeBulkMarkPurchased = async () => {
    setIsBulkRunning(true)
    try {
      await ApiService.buyOrder.bulkMarkPurchased(selectedBuyOrderIds)
      await fetchBuyOrders()
      clearSelection()
      setFeedback({
        isOpen: true,
        message: 'Compra registrada com sucesso para todas as OCs selecionadas!',
        statusOk: true,
      })
    } catch (err: any) {
      console.error('Erro ao registrar compra em lote:', err)
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message || 'Erro ao registrar compra em lote',
        statusOk: false,
      })
    } finally {
      setIsBulkRunning(false)
    }
  }

  const executeBulkConsumeFromStock = async () => {
    setIsBulkRunning(true)
    try {
      await ApiService.buyOrder.bulkConsumeFromStock(selectedBuyOrderIds)
      await fetchBuyOrders()
      clearSelection()
      setFeedback({
        isOpen: true,
        message: 'Abatimento realizado com sucesso para todas as OCs selecionadas!',
        statusOk: true,
      })
    } catch (err: any) {
      console.error('Erro ao abater em lote:', err)
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message || 'Erro ao abater do estoque em lote',
        statusOk: false,
      })
    } finally {
      setIsBulkRunning(false)
    }
  }

  const handleBulkMarkPurchased = () => {
    const count = selectedBuyOrderIds.length
    setConfirmModal({
      isOpen: true,
      title: 'Confirmar Compra em Lote',
      message: `Confirmar compra de ${count} item(ns)? Esta ação irá registrar a entrada no estoque de todos.`,
      onConfirm: executeBulkMarkPurchased,
    })
  }

  const handleBulkConsumeFromStock = () => {
    const count = selectedBuyOrderIds.length
    setConfirmModal({
      isOpen: true,
      title: 'Abater do Estoque em Lote',
      message: `Confirmar abate de ${count} item(ns)? Esta ação irá descontar do estoque e concluir as OCs selecionadas.`,
      onConfirm: executeBulkConsumeFromStock,
    })
  }

  if (isLoading) {
    return (
      <Section>
        <SectionSubtitle>Ordens de Compra</SectionSubtitle>
        <OrdenoLoading variant='inline' size='sm' text='Carregando OCs...' style={{ padding: '1.5rem 0' }} />
      </Section>
    )
  }

  if (error) {
    return (
      <Section>
        <SectionSubtitle>Ordens de Compra</SectionSubtitle>
        <p style={{ color: '#c53030', padding: '12px 0' }}>{error}</p>
      </Section>
    )
  }

  if (dedupStockInProductions.length === 0) {
    return (
      <Section>
        <SectionSubtitle>Ordens de Compra</SectionSubtitle>
        <p style={{ color: '#666', padding: '12px 0' }}>
          Nenhuma Ordem de Compra associada a esta OP.
        </p>
      </Section>
    )
  }

  const renderTable = (
    title: string,
    rows: typeof dedupStockInProductions,
    getName: (s: any) => string,
  ) => {
    const tableColSpan = 5
    const selectableIds = rows
      .map((row) => getBuyOrderFor(row.id)?.id)
      .filter((id): id is number => id != null)
    const allInTableSelected =
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedBuyOrderIds.includes(id))
    const someInTableSelected =
      selectableIds.some((id) => selectedBuyOrderIds.includes(id)) && !allInTableSelected

    return (
      <Section>
        <SectionSubtitle>{title}</SectionSubtitle>
        {rows.length > 0 ? (
          <SimpleTable className="compact-table">
            <thead>
              <tr>
                <TableHeader style={{ width: '40px', textAlign: 'center' }}>
                  {selectableIds.length > 0 && (
                    <SplitCheckbox
                      type="checkbox"
                      checked={allInTableSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someInTableSelected
                      }}
                      onChange={() => toggleSelectAllInTable(rows)}
                      title="Selecionar todos"
                    />
                  )}
                </TableHeader>
                <TableHeader>{title === 'Materiais' ? 'Material' : 'Insumo'}</TableHeader>
                <TableHeader style={{ textAlign: 'center', width: '120px' }}>
                  Un. medida
                </TableHeader>
                <TableHeader style={{ textAlign: 'right', width: '100px' }}>Qtd.</TableHeader>
                <TableHeader style={{ textAlign: 'center', width: '120px' }}>OC</TableHeader>
              </tr>
            </thead>
            <tbody>
              {rows.map((stockItem) => {
                const buyOrder = getBuyOrderFor(stockItem.id)
                const isSelected = buyOrder ? selectedBuyOrderIds.includes(buyOrder.id) : false

                return (
                  <Fragment key={stockItem.id}>
                    <SimpleTableRow
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleRow(stockItem.id)}
                    >
                      <CheckboxCell onClick={(e) => e.stopPropagation()}>
                        {buyOrder && (
                          <SplitCheckbox
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleBuyOrderSelection(buyOrder.id)}
                          />
                        )}
                      </CheckboxCell>
                      <TableCell>{getName(stockItem)}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        {stockItem.usedUnitOfMeasurement || '-'}
                      </TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {stockItem.usedQuantity != null
                          ? Number(stockItem.usedQuantity).toFixed(3)
                          : '-'}
                      </TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        <BuyOrderBadge
                          buyOrder={buyOrder as any}
                          onClick={() => toggleRow(stockItem.id)}
                        />
                      </TableCell>
                    </SimpleTableRow>
                    {expandedRows[stockItem.id] && (
                      <BuyOrderSection
                        stockInProductionId={stockItem.id}
                        productionId={servicesId}
                        existingBuyOrder={buyOrder as any}
                        onBuyOrderChange={fetchBuyOrders}
                        colSpan={tableColSpan}
                      />
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </SimpleTable>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
            Nenhum item cadastrado.
          </p>
        )}
      </Section>
    )
  }

  return (
    <>
      <SectionDivider />
      {selectedBuyOrderIds.length > 0 && (
        <BulkActionsBar>
          <BulkSelectionCount>
            {selectedBuyOrderIds.length}{' '}
            {selectedBuyOrderIds.length === 1 ? 'OC selecionada' : 'OCs selecionadas'}
          </BulkSelectionCount>
          <ActionButton
            variant="outline"
            onClick={handleBulkMarkPurchased}
            disabled={!canBulkPurchase || isBulkRunning}
            title={bulkPurchaseDisabledReason || 'Registra a entrada no estoque de todos os itens selecionados'}
          >
            {isBulkRunning ? 'Processando...' : 'Item comprado'}
          </ActionButton>
          <ActionButton
            variant="success"
            onClick={handleBulkConsumeFromStock}
            disabled={!canBulkConsume || isBulkRunning}
            title={bulkConsumeDisabledReason || 'Abate a quantidade do estoque e conclui as OCs selecionadas'}
          >
            {isBulkRunning ? 'Processando...' : 'Abater do estoque'}
          </ActionButton>
          <ActionButton variant="ghost" onClick={clearSelection} disabled={isBulkRunning}>
            Limpar seleção
          </ActionButton>
        </BulkActionsBar>
      )}
      {(type === 'materials' || type === 'all') && renderTable('Materiais', materials, getMaterialName)}
      {type === 'all' && <SectionDivider />}
      {(type === 'resources' || type === 'all') && renderTable('Insumos', resources, getResourceName)}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
            variant: 'outline',
          },
          {
            label: 'Confirmar',
            onClick: () => {
              confirmModal.onConfirm()
              setConfirmModal((prev) => ({ ...prev, isOpen: false }))
            },
            variant: 'primary',
          },
        ]}
      />
    </>
  )
}

export default FashionBuyOrdersSection
